import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiCheck, FiChevronLeft, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  country: "HK",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  postalCode: "",
  phone: "",
  paymentMethod: "card",
  cardNumber: "",
  expiry: "",
  securityCode: "",
  cardName: "",
  sameBilling: true,
  billingCountry: "HK",
  billingAddress: "",
  billingPostalCode: "",
  billingCity: "",
  acceptsTerms: false,
};

const countries = [
  ["HK", "Hong Kong"],
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["DE", "Germany"],
  ["LU", "Luxembourg"],
  ["FR", "France"],
  ["ES", "Spain"],
  ["IT", "Italy"],
  ["PT", "Portugal"],
];

const paymentMethods = [
  { id: "card", label: "Demo card", hint: "No payment will be processed" },
];

const validateCheckout = (form) => {
  const nextErrors = {};
  if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Enter a valid email address.";
  if (!form.country) nextErrors.country = "Select a delivery country.";
  if (!form.firstName.trim()) nextErrors.firstName = "Enter your first name.";
  if (!form.lastName.trim()) nextErrors.lastName = "Enter your last name.";
  if (!form.address.trim()) nextErrors.address = "Enter a delivery address.";
  if (!form.city.trim()) nextErrors.city = "Enter a city.";
  if (!form.postalCode.trim()) nextErrors.postalCode = "Enter a postal code.";

  if (form.paymentMethod === "card") {
    if (form.cardNumber.replace(/\s/g, "").length !== 16) {
      nextErrors.cardNumber = "Use a 16-digit demo card number.";
    }
    if (!/^(0[1-9]|1[0-2])\s?\/\s?\d{2}$/.test(form.expiry)) {
      nextErrors.expiry = "Use MM / YY.";
    }
    if (!/^\d{3,4}$/.test(form.securityCode)) {
      nextErrors.securityCode = "Use a 3 or 4-digit security code.";
    }
    if (!form.cardName.trim()) nextErrors.cardName = "Enter the name on card.";
  }

  if (!form.sameBilling) {
    if (!form.billingCountry) {
      nextErrors.billingCountry = "Select a billing country.";
    }
    if (!form.billingAddress.trim()) {
      nextErrors.billingAddress = "Enter a billing address.";
    }
    if (!form.billingPostalCode.trim()) {
      nextErrors.billingPostalCode = "Enter a billing postal code.";
    }
    if (!form.billingCity.trim()) {
      nextErrors.billingCity = "Enter a billing city.";
    }
  }

  if (!form.acceptsTerms) nextErrors.acceptsTerms = "Accept the store terms to continue.";
  return nextErrors;
};

const CheckOut = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = `Checkout | ${siteConfig.brandName}`;
  }, []);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const shipping =
    subtotal >= siteConfig.freeShipping.threshold
      ? 0
      : siteConfig.shipping.flatRate;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const updateCardNumber = (event) => {
    const value = event.target.value
      .replace(/\D/g, "")
      .slice(0, 16)
      .replace(/(\d{4})(?=\d)/g, "$1 ");
    setForm((current) => ({ ...current, cardNumber: value }));
    setErrors((current) => ({ ...current, cardNumber: "" }));
  };

  const updateExpiry = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
    const value = digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
    setForm((current) => ({ ...current, expiry: value }));
    setErrors((current) => ({ ...current, expiry: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateCheckout(form);

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      window.requestAnimationFrame(() => {
        document.querySelector("[aria-invalid='true']")?.focus();
      });
      return;
    }

    setIsSubmitting(true);
    await new Promise((resolve) => window.setTimeout(resolve, 650));
    const orderNumber = `TL-${String(Date.now()).slice(-8)}`;
    clearCart();
    navigate("/paysuccess", {
      replace: true,
      state: {
        orderNumber,
        total,
        email: form.email,
        paymentMethod: form.paymentMethod,
      },
    });
  };

  if (!lines.length) {
    return (
      <main className="checkout-empty">
        <p className="eyebrow">Checkout</p>
        <h1>Your bag is empty</h1>
        <p>Add a piece to your bag before starting checkout.</p>
        <Link to="/shop">Return to new arrivals</Link>
      </main>
    );
  }

  const FieldError = ({ name }) =>
    errors[name] ? (
      <span className="checkout-error" id={`${name}-error`}>
        {errors[name]}
      </span>
    ) : null;

  const fieldProps = (name) => ({
    name,
    value: form[name],
    onChange: updateField,
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${name}-error` : undefined,
  });

  return (
    <main className="checkout-page">
      <div className="checkout-brandbar">
        <Link to="/">{siteConfig.logoText}</Link>
        <span><FiLock aria-hidden="true" /> Secure demo checkout</span>
      </div>

      <div className="checkout-layout">
        <div className="checkout-form-column">
          <form onSubmit={handleSubmit} noValidate>
            <section className="checkout-section" aria-labelledby="contact-heading">
              <div className="checkout-section__heading">
                <span>01</span>
                <h1 id="contact-heading">Contact</h1>
              </div>
              <label className="checkout-field checkout-field--wide">
                <span>Email address</span>
                <input type="email" autoComplete="email" {...fieldProps("email")} />
                <FieldError name="email" />
              </label>
            </section>

            <section className="checkout-section" aria-labelledby="delivery-heading">
              <div className="checkout-section__heading">
                <span>02</span>
                <h2 id="delivery-heading">Delivery</h2>
              </div>
              <div className="checkout-fields">
                <label className="checkout-field checkout-field--wide">
                  <span>Country / region</span>
                  <select autoComplete="country" {...fieldProps("country")}>
                    {countries.map(([code, name]) => (
                      <option key={code} value={code}>{name}</option>
                    ))}
                  </select>
                  <FieldError name="country" />
                </label>
                <label className="checkout-field">
                  <span>First name</span>
                  <input autoComplete="given-name" {...fieldProps("firstName")} />
                  <FieldError name="firstName" />
                </label>
                <label className="checkout-field">
                  <span>Last name</span>
                  <input autoComplete="family-name" {...fieldProps("lastName")} />
                  <FieldError name="lastName" />
                </label>
                <label className="checkout-field checkout-field--wide">
                  <span>Company (optional)</span>
                  <input autoComplete="organization" {...fieldProps("company")} />
                </label>
                <label className="checkout-field checkout-field--wide">
                  <span>Address</span>
                  <input autoComplete="address-line1" {...fieldProps("address")} />
                  <FieldError name="address" />
                </label>
                <label className="checkout-field checkout-field--wide">
                  <span>Apartment, suite, etc. (optional)</span>
                  <input autoComplete="address-line2" {...fieldProps("apartment")} />
                </label>
                <label className="checkout-field">
                  <span>Postal code</span>
                  <input autoComplete="postal-code" {...fieldProps("postalCode")} />
                  <FieldError name="postalCode" />
                </label>
                <label className="checkout-field">
                  <span>City</span>
                  <input autoComplete="address-level2" {...fieldProps("city")} />
                  <FieldError name="city" />
                </label>
                <label className="checkout-field checkout-field--wide">
                  <span>Phone (optional)</span>
                  <input type="tel" autoComplete="tel" {...fieldProps("phone")} />
                </label>
              </div>

              <div className="delivery-method">
                <div>
                  <FiCheck aria-hidden="true" />
                  <span>
                    Standard delivery
                    <small>{siteConfig.shipping.standardDeliveryDays}</small>
                  </span>
                </div>
                <strong>{shipping === 0 ? "Free" : formatPrice(shipping)}</strong>
              </div>
            </section>

            <section className="checkout-section" aria-labelledby="payment-heading">
              <div className="checkout-section__heading">
                <span>03</span>
                <h2 id="payment-heading">Payment</h2>
              </div>
              <p className="checkout-demo-note">
                Demo only — no payment details are transmitted or processed.
              </p>
              <div className="payment-methods">
                {paymentMethods.map((method) => (
                  <label
                    key={method.id}
                    className={form.paymentMethod === method.id ? "selected" : ""}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={method.id}
                      checked={form.paymentMethod === method.id}
                      onChange={updateField}
                    />
                    <span>
                      <strong>{method.label}</strong>
                      <small>{method.hint}</small>
                    </span>
                  </label>
                ))}
              </div>

              {form.paymentMethod === "card" && (
                <div className="card-fields">
                  <label className="checkout-field checkout-field--wide">
                    <span>Card number</span>
                    <input
                      name="cardNumber"
                      inputMode="numeric"
                      autoComplete="cc-number"
                      value={form.cardNumber}
                      onChange={updateCardNumber}
                      placeholder="4242 4242 4242 4242"
                      aria-invalid={Boolean(errors.cardNumber)}
                      aria-describedby={errors.cardNumber ? "cardNumber-error" : undefined}
                    />
                    <FieldError name="cardNumber" />
                  </label>
                  <label className="checkout-field">
                    <span>Expiration</span>
                    <input
                      name="expiry"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      value={form.expiry}
                      onChange={updateExpiry}
                      placeholder="MM / YY"
                      aria-invalid={Boolean(errors.expiry)}
                      aria-describedby={errors.expiry ? "expiry-error" : undefined}
                    />
                    <FieldError name="expiry" />
                  </label>
                  <label className="checkout-field">
                    <span>Security code</span>
                    <input
                      name="securityCode"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      maxLength="4"
                      {...fieldProps("securityCode")}
                    />
                    <FieldError name="securityCode" />
                  </label>
                  <label className="checkout-field checkout-field--wide">
                    <span>Name on card</span>
                    <input autoComplete="cc-name" {...fieldProps("cardName")} />
                    <FieldError name="cardName" />
                  </label>
                </div>
              )}

              <label className="checkout-checkbox">
                <input
                  type="checkbox"
                  name="sameBilling"
                  checked={form.sameBilling}
                  onChange={updateField}
                />
                <span>Use delivery address as billing address</span>
              </label>
              {!form.sameBilling && (
                <div className="billing-address" aria-label="Billing address">
                  <p>Billing address</p>
                  <div className="checkout-fields">
                    <label className="checkout-field checkout-field--wide">
                      <span>Country / region</span>
                      <select
                        autoComplete="billing country"
                        {...fieldProps("billingCountry")}
                      >
                        {countries.map(([code, name]) => (
                          <option key={code} value={code}>{name}</option>
                        ))}
                      </select>
                      <FieldError name="billingCountry" />
                    </label>
                    <label className="checkout-field checkout-field--wide">
                      <span>Address</span>
                      <input
                        autoComplete="billing street-address"
                        {...fieldProps("billingAddress")}
                      />
                      <FieldError name="billingAddress" />
                    </label>
                    <label className="checkout-field">
                      <span>Postal code</span>
                      <input
                        autoComplete="billing postal-code"
                        {...fieldProps("billingPostalCode")}
                      />
                      <FieldError name="billingPostalCode" />
                    </label>
                    <label className="checkout-field">
                      <span>City</span>
                      <input
                        autoComplete="billing address-level2"
                        {...fieldProps("billingCity")}
                      />
                      <FieldError name="billingCity" />
                    </label>
                  </div>
                </div>
              )}
              <label className="checkout-checkbox">
                <input
                  type="checkbox"
                  name="acceptsTerms"
                  checked={form.acceptsTerms}
                  onChange={updateField}
                  aria-invalid={Boolean(errors.acceptsTerms)}
                  aria-describedby={errors.acceptsTerms ? "acceptsTerms-error" : undefined}
                />
                <span>
                  I agree to the <Link to="/terms">terms</Link> and acknowledge the{" "}
                  <Link to="/privacy">privacy policy</Link>.
                </span>
              </label>
              <FieldError name="acceptsTerms" />
            </section>

            <div className="checkout-submit-row">
              <Link to="/cartpage"><FiChevronLeft /> Return to bag</Link>
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Reviewing…" : `Pay now · ${formatPrice(total)}`}
              </button>
            </div>
          </form>
        </div>

        <aside className="checkout-summary-column" aria-label="Order summary">
          <div className="checkout-summary-inner">
            <h2>Order summary</h2>
            <div className="checkout-lines">
              {lines.map((item) => (
                <article key={`${item.id}-${item.size}-${item.color}`}>
                  <div className="checkout-line-image">
                    <img src={item.product.image} alt={item.product.name} />
                    <span>{item.quantity}</span>
                  </div>
                  <div>
                    <h3>{item.product.name}</h3>
                    <p>{item.color} · {item.size}</p>
                  </div>
                  <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                </article>
              ))}
            </div>
            <div className="checkout-totals">
              <p><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></p>
              <p><span>Delivery</span><strong>{shipping === 0 ? "Free" : formatPrice(shipping)}</strong></p>
              <p className="checkout-total"><span>Total <small>USD</small></span><strong>{formatPrice(total)}</strong></p>
            </div>
            <p className="checkout-help">Questions? Contact {siteConfig.contact.email}</p>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
